/** 图表数值快照：固定时写入，冻结展示时直接渲染 */

import { resolvePersistedChartKind, PERSISTED_CHART_KIND } from './statisticsPersistedCharts';

const LOADING_KEYS = ['chartLoading', 'trendChartLoading', 'lineCompareChartLoading', 'isStatisticsQueryLoading', 'isStatisticsQueryFetching'];

/** dataSnapshot / liveDisplayFallback 兼容：lineCompare* → trend*（重命名前已固定的图） */
const LEGACY_TREND_BUNDLE_KEYS = {
	lineCompareYearMonthsMode: 'trendYearMonthsMode',
	lineCompareDualFullPeriod: 'trendDualFullPeriod',
	lineCompareGranularity: 'trendGranularity',
	lineCompareMonthIndices: 'trendMonthIndices',
	lineComparePeriodIndices: 'trendPeriodIndices',
	lineComparePeriodYoy: 'trendPeriodYoy',
	lineCompareAxisMode: 'trendAxisMode',
	lineCompareYAxisMetric: 'trendYAxisMetric',
	lineCompareYAxisMetrics: 'trendYAxisMetrics',
	lineCompareChartType: 'trendChartType',
	lineCompareLineShape: 'trendLineShape',
	lineCompareBarOrientation: 'trendBarOrientation',
	lineCompareMetricSeriesTypes: 'trendMetricSeriesTypes',
	lineCompareDimensionCountSeries: 'trendDimensionCountSeries',
	lineCompareDimensionTotalPriceSeries: 'trendDimensionTotalPriceSeries',
	lineCompareDimensionSeriesByMetric: 'trendDimensionSeriesByMetric',
	lineCompareDimensionFieldLabel: 'trendDimensionFieldLabel',
	lineCompareChartLoading: 'trendChartLoading',
	lineCompareChartKey: 'trendChartKey',
	lineCompareScopeKey: 'trendScopeKey',
};

export const normalizeTrendChartBundleKeys = (bundle) => {
	if (!bundle || typeof bundle !== 'object') return bundle;

	let changed = false;
	const next = { ...bundle };
	for (const [legacyKey, modernKey] of Object.entries(LEGACY_TREND_BUNDLE_KEYS)) {
		if (!(legacyKey in next)) continue;
		if (next[modernKey] === undefined) {
			next[modernKey] = next[legacyKey];
		}
		delete next[legacyKey];
		changed = true;
	}
	return changed ? next : bundle;
};

const slimOrderItemsForChartSnapshot = (orderItems = []) =>
	(orderItems ?? []).map((item) => ({
		brand: item?.brand,
		supplierName: item?.supplierName,
		category: item?.category,
		name: item?.name,
		productCode: item?.productCode,
		product: item?.product,
		sellingPrice: item?.sellingPrice,
		qty: item?.qty,
	}));

/** 组成/地图类冻结快照仅保留聚合所需字段，避免整单列表撑爆请求体 */
const slimOrderForChartSnapshot = (order = {}) => ({
	_id: order._id,
	orderId: order.orderId,
	isCompleted: order.isCompleted,
	isFinalCostConfirmed: order.isFinalCostConfirmed,
	primaryPhases: order.primaryPhases,
	approvalStatus: order.approvalStatus,
	paymentStatus: order.paymentStatus,
	deliveryStatus: order.deliveryStatus,
	totalPrice: order.totalPrice,
	currentAccountsReceivable: order.currentAccountsReceivable,
	quasiReceivable: order.quasiReceivable,
	pendingPrepayment: order.pendingPrepayment,
	paidPrepayment: order.paidPrepayment,
	saleUserName: order.saleUserName,
	saleUserId: order.saleUserId,
	assistantUserId: order.assistantUserId,
	assistantUserName: order.assistantUserName,
	customer: order.customer,
	customerInfo: order.customerInfo
		? {
				customerName: order.customerInfo.customerName,
				staff_id: order.customerInfo.staff_id,
				staffName: order.customerInfo.staffName,
			}
		: undefined,
	customerCity: order.customerCity,
	paymentMethod: order.paymentMethod,
	brandName: order.brandName,
	brandNames: order.brandNames,
	createdAt: order.createdAt,
	completedAt: order.completedAt,
	orderItems: order.orderItems?.length ? slimOrderItemsForChartSnapshot(order.orderItems) : undefined,
});

export const prepareChartBundleForSnapshot = (chartBundle, chartKind) => {
	if (!chartBundle || typeof chartBundle !== 'object') return null;

	const resolvedKind = resolvePersistedChartKind({ chartKind: chartBundle.chartKind ?? chartKind });
	const next = { ...normalizeTrendChartBundleKeys(chartBundle) };

	if (resolvedKind === PERSISTED_CHART_KIND.TREND) {
		const isMapChart = next.trendChartType === 'map';
		next.orders = isMapChart ? (next.orders ?? []).map(slimOrderForChartSnapshot) : [];
		return next;
	}

	if (resolvedKind === PERSISTED_CHART_KIND.COMPOSITION) {
		next.orders = (next.orders ?? []).map(slimOrderForChartSnapshot);
		return next;
	}

	if (String(resolvedKind).startsWith('pipeline-') || resolvedKind === PERSISTED_CHART_KIND.PIPELINE_MAP) {
		next.orders = (next.orders ?? []).slice(0, 500).map(slimOrderForChartSnapshot);
		return next;
	}

	return next;
};

export const serializeChartBundleForSnapshot = (chartBundle, chartKind) => {
	const prepared = prepareChartBundleForSnapshot(chartBundle, chartKind);
	if (!prepared) return null;
	try {
		const cloned = JSON.parse(JSON.stringify(prepared));
		for (const key of LOADING_KEYS) {
			if (key in cloned) cloned[key] = false;
		}
		return cloned;
	} catch {
		return null;
	}
};

export const resolveFrozenChartBundle = (snapshot) => {
	if (!snapshot?.dataSnapshot || typeof snapshot.dataSnapshot !== 'object') return null;
	return normalizeTrendChartBundleKeys(snapshot.dataSnapshot);
};
